import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProgress } from '../../context/ProgressContext';
import { useLearning } from '../../context/LearningContext';
import { getSequenceMission } from '../../data/sequenceMissions';
import { SequenceMissionProps, MissionCompletionData } from '../../types/mission';
import SequenceChallenge, { SequenceChallengeResult } from '../../components/chess/SequenceChallenge';
// Import the new components
import { SequenceMissionBriefing, SequenceMissionResults } from '../../components/mission';
import styles from './SequenceMissionPage.module.css';
import { KnowledgeItem } from '../../types/learning';
import { createSequenceCompletionData } from '../../services/missionCompletionService';
import { mapSequenceScoreToSuperMemoGrade } from '../../utils/learningGrading';
import { useScrollToTopOnChange } from '../../hooks/useScrollToTopOnChange';

/**
 * SequenceMissionPage component
 * 
 * Handles the flow of a sequence mission: briefing, challenge, and results.
 * This is a top-level page component that manages state transitions between phases.
 */
const SequenceMissionPage: React.FC = () => {
  const SUCCESS_TRANSITION_DELAY_MS = process.env.NODE_ENV === 'test' ? 0 : 1200;
  // Get mission ID from URL params
  const { missionId } = useParams<{ missionId: string }>();
  const navigate = useNavigate();
  
  // Access the progress context for user data and mission completion
  const { userProgress, completeMission, completeSequenceChallenge, isLoading: isProgressLoading } = useProgress();

  // Access the learning context for knowledge item management
  const { knowledgeItems, knowledgeItemService, isLoading: isLearningLoading } = useLearning();

  // Component state
  const [mission, setMission] = useState<SequenceMissionProps | undefined>(undefined);
  const [missionPhase, setMissionPhase] = useState<'briefing' | 'active' | 'results'>('briefing');
  const [challengeOutcome, setChallengeOutcome] = useState<SequenceChallengeResult | null>(null);
  const [sequenceKnowledgeItem, setSequenceKnowledgeItem] = useState<KnowledgeItem | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load mission data when the component mounts or missionId changes
  useEffect(() => {
    if (missionId) {
      const foundMission = getSequenceMission(missionId);
      if (foundMission) {
        setMission(foundMission);
        setMissionPhase('briefing'); // Reset phase when mission changes
        setChallengeOutcome(null); // Reset outcome
        
        // Find or create a knowledge item for this sequence
        const existingItem = knowledgeItems.find(item => 
          item.type === 'sequence' && 
          item.content.includes(missionId)
        );
        
        if (existingItem) {
          setSequenceKnowledgeItem(existingItem);
        } else {
          // Create a new knowledge item for this sequence
          const tacticalTheme = foundMission.tacticalTheme || 'general';
          const newItem: KnowledgeItem = {
            id: `sequence-item-${missionId}`,
            content: `${tacticalTheme}: ${missionId}`,
            interval: 1,
            repetition: 0,
            efactor: 2.5,
            lastReviewed: new Date(0), // Long time ago to ensure it's due
            nextReviewDate: new Date(0), // Long time ago to ensure it's due
            performanceHistory: [],
            type: 'sequence'
          };
          
          // Save the new knowledge item
          knowledgeItemService.saveKnowledgeItem(newItem);
          setSequenceKnowledgeItem(newItem);
        }
      } else {
        // Handle mission not found, e.g., navigate to a 404 page or dashboard
        navigate('/dashboard', { replace: true });
      }
    }
  }, [missionId, navigate, knowledgeItems, knowledgeItemService]);

  // Handler for starting the mission (from briefing to active)
  const handleStartMission = () => {
    setMissionPhase('active');
  };

  // Handler for mission completion (from active to results)
  const handleMissionComplete = (result: SequenceChallengeResult) => {
    if (mission && missionId) {
      const completionData: MissionCompletionData = createSequenceCompletionData(result);
      completeMission(missionId, completionData);
      
      // Update the sequence knowledge item with the SM-2 algorithm
      if (sequenceKnowledgeItem) {
        const grade = mapSequenceScoreToSuperMemoGrade(result.score);
        completeSequenceChallenge(sequenceKnowledgeItem, grade);
      }
    }

    if (result.success && SUCCESS_TRANSITION_DELAY_MS > 0) {
      setShowCompletionModal(true);
      completionTimerRef.current = setTimeout(() => {
        setShowCompletionModal(false);
        setChallengeOutcome(result);
        setMissionPhase('results');
      }, SUCCESS_TRANSITION_DELAY_MS);
      return;
    }

    setChallengeOutcome(result);
    setMissionPhase('results');
  };

  // Handler for retrying the mission
  const handleRetry = () => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    setShowCompletionModal(false);
    setMissionPhase('briefing');
    setChallengeOutcome(null);
  };

  // Handler for continuing after completion
  const handleContinue = () => {
    // Navigate to dashboard or next mission
    navigate('/dashboard');
  };

  useEffect(() => {
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
      }
    };
  }, []);

  useScrollToTopOnChange(missionPhase);

  // Show loading state while waiting for data
  if (isProgressLoading || isLearningLoading || !mission) {
    return <div className={styles.loadingContainer}>Decrypting mission parameters...</div>;
  }

  // Keep baseline hint availability for sequence missions.
  // Difficulty can still affect scoring/penalties inside the challenge flow.
  const shouldShowHints = true;

  return (
    <div 
      className={styles.sequenceMissionPageContainer} 
      data-testid="sequence-mission-page"
      role="main"
      aria-label={`Sequence Mission: ${mission.title}`}
    >
      {showCompletionModal && (
        <div className={styles.completionModalOverlay} data-testid="sequence-mission-complete-modal">
          <div className={styles.completionModal}>
            <h2 className={styles.completionTitle}>Mission Accomplished</h2>
            <p className={styles.completionBody}>Debriefing report is being prepared...</p>
          </div>
        </div>
      )}
      {missionPhase === 'briefing' && (
        <SequenceMissionBriefing
          mission={mission}
          onStartMission={handleStartMission}
          onBack={() => navigate(-1)}
        />
      )}

      {missionPhase === 'active' && (
        <SequenceChallenge
          sequence={{
            id: mission.id,
            moves: mission.sequence,
            startingPosition: mission.position,
            tacticalTheme: mission.tacticalTheme,
            difficulty: mission.difficulty,
            description: mission.description
          }}
          difficulty={mission.difficulty}
          timeLimit={mission.timeLimit ?? undefined}
          showHints={shouldShowHints}
          onComplete={handleMissionComplete}
          enableKeyboardNavigation={true}
        />
      )}

      {missionPhase === 'results' && challengeOutcome && (
        <SequenceMissionResults
          mission={mission}
          result={challengeOutcome}
          onContinue={handleContinue}
          onRetry={handleRetry}
        />
      )}
    </div>
  );
};

export default SequenceMissionPage;
